//Custom components
import WidgetLayout from '../WidgetLayout';
import Card from '../../UI/Card';
import BarChart from '../../UI/Charts/BarChart';

//Services
import { useDashboardData } from '../../../hooks/ReactQueryHooks/useDashboardQuery';

//Assets and miscellaneous
import { DashboardType } from '../../../types/Analytics';
import { CurrencyFormatDisplay } from '../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { Widget } from '../constants';
import { useSelector } from 'react-redux';
import { accountSelector } from '../../../store';

interface ProjectBudgetSummaryWidgetProps {
  dashboard: DashboardType;
}

interface BudgetDashboardDataInterface {
  name: string;
  amount: number;
}

interface BudgetSummaryDashboardDataInterface {
  id: number;
  name: string;
  budget: BudgetDashboardDataInterface[];
}

const ProjectBudgetSummaryWidget = ({
  dashboard,
}: ProjectBudgetSummaryWidgetProps) => {
  const { data: currentClientAccount } = useSelector(accountSelector);
  const fetchProjectBudgetSummaryWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECT_BUDGET_SUMMARY,
  });

  const projectBudgetSummaryWidgetData =
    fetchProjectBudgetSummaryWidgetData?.data?.data;

  const projectsName: string[] = projectBudgetSummaryWidgetData?.map(
    (project: BudgetSummaryDashboardDataInterface) => project.name
  );
  const projectSources: number[] = projectBudgetSummaryWidgetData?.map(
    (project: BudgetSummaryDashboardDataInterface) => {
      let sourcesTotal = 0;
      if (project.budget) {
        sourcesTotal += project.budget.reduce(
          (acc, next) => acc + next.amount,
          0
        );
      }
      return sourcesTotal;
    }
  );

  const totalDevelopmentValue = projectSources?.reduce(
    (acc, next) => acc + next,
    0
  );

  const cclfApprovedLoans: number[] = projectBudgetSummaryWidgetData?.map(
    (project: any) => {
      let sourcesTotal = 0;

      if (project.budget) {
        sourcesTotal += project.budget.reduce((acc: any, next: any) => {
          if (next.name.toLowerCase() === 'cclf loan') {
            return acc + next.amount;
          } else {
            return acc + 0;
          }
        }, 0);
      }
      return sourcesTotal;
    }
  );

  const totalCCLFApprovedLoans = cclfApprovedLoans?.reduce(
    (acc, next) => acc + next,
    0
  );

  return (
    <>
      <WidgetLayout
        dashboard={dashboard}
        widget={Widget.PROJECT_BUDGET_SUMMARY}
      >
        <section className='flex gap-8 h-full'>
          <Card
            title='Project Budget Summary'
            error={fetchProjectBudgetSummaryWidgetData.isError}
          >
            {projectBudgetSummaryWidgetData && (
              <>
                <div className='flex flex-row justify-between items-center px-8 py-4'>
                  <span className='flex flex-col'>
                    <p className='text-gray-400 text-sm'>
                      Total Development Value
                    </p>
                    <p className='text-black-800 font-bold text-lg'>
                      <CurrencyFormatDisplay
                        value={totalDevelopmentValue}
                        prefix='$'
                      />
                    </p>
                  </span>

                  {currentClientAccount.subdomain === 'chicagorebuild' && (
                    <span className='flex flex-col'>
                      <p className='text-gray-400 text-sm'>
                        Rebuild Loans Approved
                      </p>
                      <p className='text-tertiaryColor-normal font-bold text-lg'>
                        <CurrencyFormatDisplay
                          value={totalCCLFApprovedLoans}
                          prefix='$'
                        />
                      </p>
                    </span>
                  )}
                </div>
                <div className='p-5 max-w-full'>
                  <BarChart
                    dataLabels={projectsName}
                    colors={false}
                    datasets={projectSources}
                  />
                </div>
              </>
            )}
          </Card>
        </section>
      </WidgetLayout>
    </>
  );
};

export default ProjectBudgetSummaryWidget;
